import {types} from 'mobx-state-tree';

export enum ChatButtonKind {
  LOCAL = 'local',
  WEB_URL = 'web_url',
  POSTBACK = 'postback',
  POSTBACK2 = 'postback2'
}

export const LocalButton = types.model('LocalButton', {
  kind: types.literal(ChatButtonKind.LOCAL),
  title: types.string,
  action: types.string
});

export type LocalButtonType = typeof LocalButton.Type;

export const WebUrlButton = types.model('WebUrlButton', {
  kind: types.literal(ChatButtonKind.WEB_URL),
  title: types.string,
  url: types.string
});

export type WebUrlButtonType = typeof WebUrlButton.Type;

export const PostbackButton = types.model('PostbackButton', {
  kind: types.literal(ChatButtonKind.POSTBACK),
  title: types.string,
  payload: types.string
});

export type PostbackButtonType = typeof PostbackButton.Type;

export const Postback2Button = types.model('Postback2Button', {
  kind: types.literal(ChatButtonKind.POSTBACK2),
  title: types.string,
  payload: types.string
});

export type Postback2ButtonType = typeof Postback2Button.Type;

export const ChatButton = types.union(
  LocalButton,
  WebUrlButton,
  PostbackButton,
  Postback2Button
);

export type ChatButtonType =
  | LocalButtonType
  | WebUrlButtonType
  | PostbackButtonType
  | Postback2ButtonType;

export enum MessageAttachmentKind {
  FILE = 'file',
  IMAGE = 'image',
  TEMPLATE = 'template',
  EVENT = 'event',
  MULTIPLE_IMAGE = 'multiple_image'
}

export enum MessageAttachmentTemplateKind {
  BUTTONS = 'button',
  GENERIC = 'generic',
  GUEST_TICKET = 'guest_ticket'
}

export const FileAttachment = types.model('FileAttachment', {
  kind: types.literal(MessageAttachmentKind.FILE),
  payload: types.model({url: types.string})
});

export type FileAttachmentType = typeof FileAttachment.Type;

export const ImageAttachment = types.model('ImageAttachment', {
  kind: types.literal(MessageAttachmentKind.IMAGE),
  payload: types.model({url: types.string})
});

export type ImageAttachmentType = typeof ImageAttachment.Type;

export const MultipleImageAttachment = types.model('MultipleImageAttachment', {
  kind: types.literal(MessageAttachmentKind.MULTIPLE_IMAGE),
  payload: types.model({urls: types.array(types.string)})
});

export type MultipleImageAttachmentType = typeof MultipleImageAttachment.Type;

export const MessageTemplateElement = types.model('MessageTemplateElement', {
  title: types.string,
  subtitle: types.maybe(types.string),
  imageUrl: types.maybe(types.string),
  defaultAction: types.maybe(ChatButton),
  buttons: types.array(ChatButton),
  data: types.map(
    types.union(
      types.string,
      types.number,
      types.boolean,
      types.model({
        userId: types.number,
        role: types.string
      })
    )
  ),
  imageShape: types.maybe(types.string)
});

export const TicketTemplateElement = types.model('TicketTemplateElement', {
  title: types.string,
  subtitle: types.maybe(types.string),
  buttons: types.array(ChatButton),
  data: types.model({
    ticket: types.model({
      guestName: types.maybe(types.string),
      budgetMin: types.maybe(types.number),
      budgetCurrency: types.maybe(types.string),
      guestType: types.string,
      state: types.string,
      location: types.string,
      expireTime: types.string,
      id: types.number,
      budgetMax: types.maybe(types.number)
    })
  })
});

export type TicketTemplateElementType = typeof TicketTemplateElement.Type;

export const GenericTemplateAttachment = types.model(
  'GenericTemplateAttachment',
  {
    kind: types.literal(MessageAttachmentKind.TEMPLATE),
    payload: types.model({
      templateKind: types.literal(MessageAttachmentTemplateKind.GENERIC),
      text: types.maybe(types.string),
      elements: types.array(MessageTemplateElement)
    })
  }
);

export const ButtonsTemplateAttachment = types.model(
  'ButtonsTemplateAttachment',
  {
    kind: types.literal(MessageAttachmentKind.TEMPLATE),
    payload: types.model({
      templateKind: types.literal(MessageAttachmentTemplateKind.BUTTONS),
      buttons: types.array(ChatButton)
    })
  }
);

export const GuestTicketTemplateAttachment = types.model(
  'GuestTicketTemplateAttachment',
  {
    kind: types.literal(MessageAttachmentKind.TEMPLATE),
    payload: types.model({
      templateKind: types.literal(MessageAttachmentTemplateKind.GUEST_TICKET),
      elements: types.array(TicketTemplateElement),
      text: types.maybe(types.string)
    })
  }
);

export const EventAttachment = types.model('EventAttachment', {
  kind: types.literal(MessageAttachmentKind.EVENT),
  payload: types.model({
    text: types.string
  })
});

export const TextFormat = types.model('TextFormat', {
  text: types.string || undefined
});

export type TextFormatType = typeof TextFormat.Type;

export const FileFormat = types.model('FileFormat', {
  attachment: FileAttachment
});

export type FileFormatType = typeof FileFormat.Type;

export const ImageFormat = types.model('ImageFormat', {
  attachment: ImageAttachment
});

export type ImageFormatType = typeof ImageFormat.Type;

export const MultipleImageFormat = types.model('MultipleImageFormat', {
  attachment: MultipleImageAttachment
});

export type MultipleImageFormatType = typeof MultipleImageFormat.Type;

export const GenericTemplateFormat = types.model('GenericTemplateFormat', {
  attachment: GenericTemplateAttachment
});

export type GenericTemplateFormatType = typeof GenericTemplateFormat.Type;

export const ButtonsTemplateFormat = types.model('ButtonsTemplateFormat', {
  attachment: ButtonsTemplateAttachment
});

export type ButtonsTemplateFormatType = typeof ButtonsTemplateFormat.Type;

export const TicketsTemplateFormat = types.model('TicketsTemplateFormat', {
  attachment: GuestTicketTemplateAttachment
});

export type TicketsTemplateFormatType = typeof TicketsTemplateFormat.Type;

export const EventFormat = types.model('EventFormat', {
  attachment: EventAttachment
});

export type EventFormatType = typeof EventFormat.Type;

export const TranslatedTextModel = types.model('TranslatedText', {
  text: types.string,
  originText: types.string
});

export const TranslationFormat = types.model('TranslationFormat', {
  translation: TranslatedTextModel
});

export type TranslationFormatType = typeof TranslationFormat.Type;

const MessageFormat = types.union(
  TextFormat,
  FileFormat,
  ImageFormat,
  GenericTemplateFormat,
  ButtonsTemplateFormat,
  EventFormat,
  TranslationFormat,
  MultipleImageFormat,
  TicketsTemplateFormat
);

export default MessageFormat;

export type MessageFormatType =
  | TextFormatType
  | FileFormatType
  | ImageFormatType
  | GenericTemplateFormatType
  | ButtonsTemplateFormatType
  | EventFormatType
  | TranslationFormatType
  | MultipleImageFormatType
  | TicketsTemplateFormatType;



// WEBPACK FOOTER //
// ./src/models/chatChannelModel/message/format/MessageFormat.ts